import React, { useState, useMemo } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, Filter, CalendarIcon, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCalls } from '@/hooks/api/useCalls';
import { CallDirection, CallStatus, FilterCallsDto } from '@/services/api/calls.api';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 15;

const statusConfig: Record<CallStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  [CallStatus.RINGING]: { label: 'Sonnerie', variant: 'outline' },
  [CallStatus.IN_PROGRESS]: { label: 'En cours', variant: 'default' },
  [CallStatus.COMPLETED]: { label: 'Terminé', variant: 'secondary' },
  [CallStatus.MISSED]: { label: 'Manqué', variant: 'destructive' },
  [CallStatus.FAILED]: { label: 'Échoué', variant: 'destructive' },
  [CallStatus.BUSY]: { label: 'Occupé', variant: 'outline' },
  [CallStatus.NO_ANSWER]: { label: 'Sans réponse', variant: 'outline' },
};

const directionConfig: Record<CallDirection, string> = {
  [CallDirection.INBOUND]: 'Entrant',
  [CallDirection.OUTBOUND]: 'Sortant',
};

function DirectionIcon({ direction }: { direction: CallDirection }) {
  if (direction === CallDirection.INBOUND) {
    return <PhoneIncoming className="h-4 w-4 text-primary" />;
  }
  return <PhoneOutgoing className="h-4 w-4 text-accent-foreground" />;
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface CallHistoryProps {
  className?: string;
  dealId?: string;
  limit?: number;
}

export function CallHistory({ className, dealId, limit }: CallHistoryProps) {
  const [statusFilter, setStatusFilter] = useState<CallStatus | 'all'>('all');
  const [directionFilter, setDirectionFilter] = useState<CallDirection | 'all'>('all');
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [page, setPage] = useState(0);

  const filters = useMemo<FilterCallsDto | undefined>(() => {
    const f: FilterCallsDto = {};
    if (dealId) f.dealId = dealId;
    if (statusFilter !== 'all') f.status = statusFilter;
    if (directionFilter !== 'all') f.direction = directionFilter;
    if (fromDate) f.fromDate = fromDate.toISOString();
    if (toDate) f.toDate = toDate.toISOString();
    return Object.keys(f).length > 0 ? f : undefined;
  }, [dealId, statusFilter, directionFilter, fromDate, toDate]);

  const { data: calls, isLoading, error } = useCalls(filters);

  // Reset page when filters change
  const filterKey = JSON.stringify(filters);
  const prevFilterKey = React.useRef(filterKey);
  if (prevFilterKey.current !== filterKey) {
    prevFilterKey.current = filterKey;
    if (page !== 0) setPage(0);
  }

  const allCalls = limit && calls ? calls.slice(0, limit) : calls;
  const totalCount = allCalls?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const displayCalls = allCalls?.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const hasActiveFilters = statusFilter !== 'all' || directionFilter !== 'all' || fromDate || toDate;

  const clearFilters = () => {
    setStatusFilter('all');
    setDirectionFilter('all');
    setFromDate(undefined);
    setToDate(undefined);
    setPage(0);
  };

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Phone className="h-5 w-5 text-primary" />
          Historique des appels
          {allCalls && (
            <Badge variant="secondary">{totalCount}</Badge>
          )}
        </CardTitle>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Filter className="h-4 w-4 text-muted-foreground" />

          {/* Direction filter */}
          <Select
            value={directionFilter}
            onValueChange={(v) => setDirectionFilter(v as CallDirection | 'all')}
          >
            <SelectTrigger className="w-[140px] h-8 text-xs">
              <SelectValue placeholder="Direction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes directions</SelectItem>
              {Object.entries(directionConfig).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status filter */}
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as CallStatus | 'all')}
          >
            <SelectTrigger className="w-[160px] h-8 text-xs">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(statusConfig).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* From date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("h-8 text-xs gap-1", !fromDate && "text-muted-foreground")}>
                <CalendarIcon className="h-3.5 w-3.5" />
                {fromDate ? format(fromDate, 'dd/MM/yyyy') : 'Date début'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={fromDate}
                onSelect={setFromDate}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>

          {/* To date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("h-8 text-xs gap-1", !toDate && "text-muted-foreground")}>
                <CalendarIcon className="h-3.5 w-3.5" />
                {toDate ? format(toDate, 'dd/MM/yyyy') : 'Date fin'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={toDate}
                onSelect={setToDate}
                initialFocus
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-muted-foreground" onClick={clearFilters}>
              <X className="h-3.5 w-3.5" />
              Réinitialiser
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Clock className="h-5 w-5 animate-spin mr-2" />
            Chargement…
          </div>
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            Erreur lors du chargement des appels
          </div>
        ) : !displayCalls || displayCalls.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <PhoneMissed className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p>Aucun appel enregistré</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>De</TableHead>
                  <TableHead>Vers</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayCalls.map((call) => {
                  const status = statusConfig[call.status] ?? { label: call.status, variant: 'outline' as const };
                  return (
                    <TableRow key={call.id}>
                      <TableCell>
                        <DirectionIcon direction={call.direction} />
                      </TableCell>
                      <TableCell className="font-medium">{call.from}</TableCell>
                      <TableCell>{call.to}</TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDuration(call.duration)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(call.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <span className="text-xs text-muted-foreground">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} sur {totalCount}
                </span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground px-2">
                    {page + 1} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
